import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { searchDirectoryUsers } from '../../api-services/UserProfileApi';
import { DirectoryUser } from '../../helpers/InterfacesServer';

interface Props {
    value: string;
    onChange: (value: string) => void;
    onCommit: (value: string) => void;
    onBlur: () => void;
}

interface RecipientOption {
    id: string;
    kind: 'directory' | 'other';
    label: string;
}

const otherOption: RecipientOption = { id: '__other__', kind: 'other', label: 'Other' };
const directoryLabelPattern = /\([^()@\s]+@[^()\s]+\)$/;

function userLabel(user: DirectoryUser) {
    const email = user.email || user.userPrincipalName;
    const name = user.fullName || [user.firstName, user.lastName].filter(Boolean).join(' ') || email;
    return email && name.toLocaleLowerCase() !== email.toLocaleLowerCase() ? `${name} (${email})` : name;
}

function directoryOption(user: DirectoryUser): RecipientOption {
    return { id: user.id || user.email || user.userPrincipalName, kind: 'directory', label: userLabel(user) };
}

function existingDirectoryOption(value: string): RecipientOption | null {
    return value && directoryLabelPattern.test(value)
        ? { id: `existing:${value}`, kind: 'directory', label: value }
        : null;
}

export function IncidentEscalationRecipient({ value, onChange, onCommit, onBlur }: Props) {
    const [initialDirectoryOption] = useState(() => existingDirectoryOption(value));
    const [selected, setSelected] = useState<RecipientOption | null>(() => initialDirectoryOption ?? (value ? otherOption : null));
    const [directoryOptions, setDirectoryOptions] = useState<RecipientOption[]>([]);
    const [inputValue, setInputValue] = useState(initialDirectoryOption?.label ?? (value ? otherOption.label : ''));
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const isOther = selected?.kind === 'other';

    useEffect(() => {
        const query = searchText.trim();
        if (query.length < 2) {
            setDirectoryOptions([]);
            setLoading(false);
            return;
        }

        let active = true;
        const timer = window.setTimeout(async () => {
            setLoading(true);
            try {
                const users = await searchDirectoryUsers(query);
                if (active) setDirectoryOptions(users.map(directoryOption));
            } catch {
                if (active) setDirectoryOptions([]);
            } finally {
                if (active) setLoading(false);
            }
        }, 400);

        return () => {
            active = false;
            window.clearTimeout(timer);
        };
    }, [searchText]);

    const options = useMemo(() => {
        const byId = new Map<string, RecipientOption>();
        if (selected?.kind === 'directory') byId.set(selected.id, selected);
        directoryOptions.forEach(option => byId.set(option.id, option));
        byId.set(otherOption.id, otherOption);
        return Array.from(byId.values());
    }, [directoryOptions, selected]);

    return <>
        <Autocomplete<RecipientOption, false, false, false>
            fullWidth
            size="small"
            openOnFocus
            options={options}
            value={selected}
            inputValue={inputValue}
            loading={loading}
            filterOptions={items => items}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, current) => option.id === current.id}
            onInputChange={(_, nextInputValue, reason) => {
                setInputValue(nextInputValue);
                if (reason === 'input') setSearchText(nextInputValue);
                if (reason === 'clear') setSearchText('');
            }}
            onChange={(_, option) => {
                setSelected(option);
                setSearchText('');
                if (!option) {
                    onChange('');
                    return;
                }
                if (option.kind === 'other') {
                    onChange('');
                    return;
                }
                onCommit(option.label);
            }}
            renderInput={params => <TextField {...params}
                placeholder="Type a name or email to search users"
                helperText="Search the user directory, or select Other to enter a person manually."
                InputProps={{ ...params.InputProps, endAdornment: <>{loading && <CircularProgress color="inherit" size={20} />}{params.InputProps.endAdornment}</> }}
            />}
        />
        {isOther && <TextField
            fullWidth
            required
            autoFocus
            size="small"
            sx={{ mt: 2 }}
            label="Other Person / Authority"
            placeholder="Enter the personnel or authority"
            value={value}
            inputProps={{ maxLength: 200 }}
            onChange={event => onChange(event.target.value)}
            onBlur={onBlur}
        />}
    </>;
}
